import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { ReportNameEnum, OrderBusinessStatusEnum } from "../utils/enums";
import { parseISO, format, subMonths, subDays, startOfMonth, endOfMonth } from "date-fns";

const REPORT_GENERATE_V2_PATH = "/v1/pocas/reports/generate";
const ASYNC_THRESHOLD_DAYS = 90;
const LOTTIE_LOADER_SRC = "/animations/loader.lottie";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [reportName, setReportName] = useState("MIS"); 
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
  const [notificationEmails, setNotificationEmails] = useState("");
  const [sendOnEmail, setSendOnEmail] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState("");
  const [statusDropdownPosition, setStatusDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const statusDropdownRef = useRef(null);
  const statusMenuRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [successState, setSuccessState] = useState(null); // 'sync' | 'async' | null
  const [fetchingReports, setFetchingReports] = useState(true);
  const [latestReport, setLatestReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterReportType, setFilterReportType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'report_generated_date_time', direction: 'desc' });
  const [downloadingId, setDownloadingId] = useState(null);

  const statusOptions = useMemo(() => Object.entries(OrderBusinessStatusEnum).map(([key, { code, name }]) => ({ code, name })), []);

  const getDaysBetween = (from, to) => {
    if (!from || !to) return 0;
    const fromD = new Date(from);
    const toD = new Date(to);
    return Math.round((toD - fromD) / (24 * 60 * 60 * 1000)) + 1;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = statusDropdownRef.current?.contains(event.target);
      const inMenu = statusMenuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) setStatusDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateStatusDropdownPosition = () => {
    if (statusDropdownRef.current) {
      const rect = statusDropdownRef.current.getBoundingClientRect();
      setStatusDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 280),
      });
    }
  };

  useEffect(() => {
    if (!statusDropdownOpen) return;
    updateStatusDropdownPosition();
    const onScrollOrResize = () => updateStatusDropdownPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [statusDropdownOpen]);

  const filteredStatusOptions = useMemo(() => {
    if (!statusSearch.trim()) return statusOptions;
    const q = statusSearch.trim().toLowerCase();
    return statusOptions.filter(({ name }) => name.toLowerCase().includes(q));
  }, [statusOptions, statusSearch]);

  useEffect(() => {
    fetchReports();
  }, []);

  // Auto-enable "Email when ready" when date range > 90 days (mandatory per API)
  useEffect(() => {
    if (fromDate && toDate && getDaysBetween(fromDate, toDate) > ASYNC_THRESHOLD_DAYS) {
      setSendOnEmail(true);
    }
  }, [fromDate, toDate]);

  const fetchReports = async () => {
    setFetchingReports(true);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}${process.env.REACT_APP_FETCH_ALL_REPORTS}`;

      const params = new URLSearchParams({
        merchantId: merchantId,
      }).toString();


      const response = await axios.get(`${apiUrl}?${params}`, {
        headers: {
          'Authorization': authToken,
          'x-merchant-id': merchantId
        }
      });

      const arr = Array.isArray(response.data) ? response.data : [];
      const sortedReports = arr
         .slice()
         .sort((a, b) =>
           new Date(b.report_generated_date_time) - new Date(a.report_generated_date_time)
         );

      setReports(sortedReports);
      if (sortedReports.length > 0) {
        setLatestReport(sortedReports[0]);
      } else {
        setLatestReport(null);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to fetch reports");
    } finally {
      setFetchingReports(false);
    }
  };

  const generateReport = async (e) => {
    e.preventDefault(); 
    
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From Date cannot be after To Date");
      return;
    }

    const days = getDaysBetween(fromDate, toDate);
    const requiresEmail = sendOnEmail || days > ASYNC_THRESHOLD_DAYS;
    const emailsList = notificationEmails
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (requiresEmail && emailsList.length === 0) {
      toast.error(
        days > ASYNC_THRESHOLD_DAYS
          ? "Reports for more than 90 days are sent by email. Please add at least one notification email."
          : "Please add at least one notification email to receive the report."
      );
      return;
    }

    setLoading(true);

    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");
    const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}${REPORT_GENERATE_V2_PATH}`;

    const requestBody = {
      merchant_id: merchantId,
      from_date: fromDate,
      to_date: toDate,
      current_status_codes: selectedStatusCodes.length > 0 ? selectedStatusCodes : undefined,
      notification_emails: emailsList.length > 0 ? emailsList : undefined,
      ...(sendOnEmail ? { async: true } : {}),
    };
    if (requestBody.current_status_codes === undefined) delete requestBody.current_status_codes;
    if (requestBody.notification_emails === undefined) delete requestBody.notification_emails;

    try {
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          Authorization: authToken,
          "x-merchant-id": merchantId,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      // 200 OK — sync report ready (snake_case response): download instantly + success UI
      if (response.status === 200) {
        if (data.report_url) {
          window.open(data.report_url, "_blank", "noopener,noreferrer");
        }
        setSuccessState("sync");
        fetchReports();
        setFromDate("");
        setToDate("");
        setNotificationEmails("");
        setSendOnEmail(false);
        toast.success("Report generated. Download started.");
        setTimeout(() => setSuccessState(null), 2500);
        return;
      }

      // 202 Accepted — async report queued (snake_case response): success UI
      if (response.status === 202) {
        const message = data.message || "Report generation queued. You will receive an email when the report is ready.";
        setSuccessState("async");
        setFromDate("");
        setToDate("");
        setNotificationEmails("");
        setSendOnEmail(false);
        toast.success(message);
        setTimeout(() => setSuccessState(null), 2500);
        return;
      }

      // Any other success status
      toast.success("Request accepted.");
    } catch (error) {
      const status = error.response?.status;
      const body = error.response?.data;

      let message = "Failed to generate report.";

      if (error.response) {
        message = body?.message || body?.error || message;
        if (typeof body?.errors === "object" && Object.keys(body.errors).length > 0) {
          const first = Object.entries(body.errors)[0];
          message = first[1]?.message || first[1] || message;
        }
        if (Array.isArray(body?.errors) && body.errors.length > 0) {
          message = body.errors[0]?.message || body.errors[0] || message;
        }
        if (status === 401) message = message || "Session expired. Please log in again.";
        if (status === 403) message = message || "You do not have permission to generate this report.";
        if (status === 404) message = message || "Report service is not available.";
        if (status >= 500) message = message || "Server error. Please try again later.";
      } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        message = "Network error. Please check your connection and try again.";
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
      console.error("Report generation error:", error);
    } finally {
      setLoading(false);
    }
  };

const formatDateTime = (isoString) => {
  return format(parseISO(isoString), "dd MMM yyyy, hh:mm:ss a")
    .replace("AM", "am")
    .replace("PM", "pm");
};

const setDatePreset = (preset) => {
  const today = new Date();
  let from, to;

  switch (preset) {
    case 'today':
      from = to = format(today, 'yyyy-MM-dd');
      break;
    case 'yesterday':
      from = to = format(subDays(today, 1), 'yyyy-MM-dd');
      break;
    case 'last7days':
      from = format(subDays(today, 7), 'yyyy-MM-dd');
      to = format(today, 'yyyy-MM-dd');
      break;
    case 'last30days':
      from = format(subDays(today, 30), 'yyyy-MM-dd');
      to = format(today, 'yyyy-MM-dd');
      break;
    case 'thisMonth':
      from = format(startOfMonth(today), 'yyyy-MM-dd');
      to = format(endOfMonth(today), 'yyyy-MM-dd');
      break;
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      from = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
      to = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
      break;
    default:
      return;
  }

  setFromDate(from);
  setToDate(to);
};

const handleDownload = async (report) => {
  setDownloadingId(report.report_generated_date_time);
  try {
    window.open(report.report_url, '_blank');
    toast.success("Download started");
  } catch (error) {
    toast.error("Failed to download report");
  } finally {
    setTimeout(() => setDownloadingId(null), 1000);
  }
};

// Statistics calculation
// Filtered and sorted reports
const filteredAndSortedReports = useMemo(() => {
  let filtered = reports.filter(report => {
    const matchesSearch = 
      report.report_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.report_generated_by.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.generated_from_date.includes(searchQuery) ||
      report.generated_to_date.includes(searchQuery);
    
    const matchesFilter = filterReportType === 'all' || report.report_name === filterReportType;
    
    return matchesSearch && matchesFilter;
  });

  // Sorting
  filtered.sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'report_generated_date_time') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return filtered;
}, [reports, searchQuery, filterReportType, sortConfig]);

// Pagination
const paginatedReports = useMemo(() => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  return filteredAndSortedReports.slice(startIndex, startIndex + itemsPerPage);
}, [filteredAndSortedReports, currentPage, itemsPerPage]);

const totalPages = Math.ceil(filteredAndSortedReports.length / itemsPerPage);

const handleSort = (key) => {
  setSortConfig(prev => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
  }));
};

const uniqueReportTypes = useMemo(() => {
  return [...new Set(reports.map(r => r.report_name))];
}, [reports]);

return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 mt-2">
    <div className="container mx-auto max-w-[1600px] px-2">
      
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Reports Dashboard
            </h3>
            <p className="text-gray-600">Generate, manage and download your shipment orders reports</p>
          </div>
          <button
            onClick={fetchReports}
            disabled={fetchingReports}
            className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`w-5 h-5 ${fetchingReports ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Report Generation Form — responsive, single-flow layout */}
      <form
        onSubmit={generateReport}
        className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200/80 overflow-hidden relative"
      >
        {/* Top: header bar */}
        <div className="flex items-center gap-2 px-4 py-3 bg-blue-700 text-white">
          <svg className="w-5 h-5 shrink-0 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-base font-semibold">Generate Report</span>
        </div>

        <div className="p-4 sm:p-5 space-y-4 min-w-0 relative">
          {/* Loading overlay — sync/async request in progress */}
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-b-xl bg-white/95 backdrop-blur-sm">
              <div className="w-20 h-20 sm:w-24 sm:h-24">
                <DotLottieReact src={LOTTIE_LOADER_SRC} loop autoplay style={{ width: "100%", height: "100%" }} />
              </div>
              <p className="mt-3 text-sm font-semibold text-gray-800">Generating report...</p>
              <p className="mt-1 text-xs text-gray-500">Please wait</p>
            </div>
          )}

          {/* Success overlay — sync: report ready / async: queued */}
          {successState && !loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-b-xl bg-white/98 backdrop-blur-sm">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-emerald-100 flex items-center justify-center ring-4 ring-emerald-50">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="mt-3 text-sm font-bold text-gray-900">
                {successState === "sync" ? "Report ready!" : "Report queued!"}
              </p>
              <p className="mt-1 text-xs text-gray-600 text-center px-4">
                {successState === "sync"
                  ? "Download started. Check your browser or new tab."
                  : "You will receive an email when the report is ready."}
              </p>
            </div>
          )}
            {/* Row 1: Type | From | To | Status — responsive grid, wider fields to use space */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-end">
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">Type</label>
                <select
                  className="w-full h-9 sm:h-10 px-3 border border-gray-200 rounded-md text-sm bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none transition-colors"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                >
                  {Object.values(ReportNameEnum).map((report) => (
                    <option key={report.id} value={report.name}>{report.name}</option>
                  ))}
                </select>
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">From</label>
                <input
                  type="date"
                  className="w-full h-9 sm:h-10 px-3 border border-gray-200 rounded-md text-sm bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none transition-colors"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={toDate || undefined}
                  required
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">To</label>
                <input
                  type="date"
                  className="w-full h-9 sm:h-10 px-3 border border-gray-200 rounded-md text-sm bg-gray-50/50 focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none transition-colors"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  min={fromDate || undefined}
                  required
                />
              </div>
              <div className="relative min-w-0 col-span-2 lg:col-span-1" ref={statusDropdownRef}>
                <label className="block text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">Status</label>
                <button
                  type="button"
                  onClick={() => {
                    if (!statusDropdownOpen && statusDropdownRef.current) {
                      const rect = statusDropdownRef.current.getBoundingClientRect();
                      setStatusDropdownPosition({
                        top: rect.bottom + 4,
                        left: rect.left,
                        width: Math.max(rect.width, 280),
                      });
                      setStatusSearch("");
                    }
                    setStatusDropdownOpen((o) => !o);
                  }}
                  className="w-full h-9 sm:h-10 px-3 border border-gray-200 rounded-md text-sm text-left flex items-center justify-between bg-gray-50/50 hover:bg-white focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none transition-colors"
                >
                  <span className="text-gray-700 truncate">
                    {selectedStatusCodes.length === 0 ? "All" : `${selectedStatusCodes.length} selected`}
                  </span>
                  <svg className={`w-3.5 h-3.5 text-gray-400 shrink-0 ml-1 ${statusDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {statusDropdownOpen && createPortal(
                  <div
                    ref={statusMenuRef}
                    className="fixed z-[9999] bg-white rounded-xl border border-gray-200 shadow-2xl ring-1 ring-black/5 flex flex-col min-w-[20rem] max-w-[22rem] max-h-[min(70vh,28rem)]"
                    style={{
                      top: statusDropdownPosition.top,
                      left: statusDropdownPosition.left,
                      width: statusDropdownPosition.width,
                    }}
                  >
                    <div className="p-2 border-b border-gray-100 shrink-0">
                      <input
                        type="text"
                        placeholder="Search status..."
                        value={statusSearch}
                        onChange={(e) => setStatusSearch(e.target.value)}
                        className="w-full h-9 pl-3 pr-9 py-2 border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none bg-gray-50/50 focus:bg-white"
                        autoFocus
                      />
                      <div className="flex gap-1.5 mt-2">
                        <button
                          type="button"
                          onClick={() => setSelectedStatusCodes(statusOptions.map((o) => o.code))}
                          className="flex-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedStatusCodes([])}
                          className="flex-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md border border-gray-200"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="overflow-y-auto overscroll-contain py-1 flex-1">
                      {filteredStatusOptions.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-gray-500 text-center">No statuses match your search</p>
                      ) : (
                        filteredStatusOptions.map(({ code, name }) => (
                          <label
                            key={code}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50/80 cursor-pointer group"
                          >
                            <input
                              type="checkbox"
                              checked={selectedStatusCodes.includes(code)}
                              onChange={() => {
                                setSelectedStatusCodes((prev) =>
                                  prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
                                );
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">{name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/50 rounded-b-xl text-xs text-gray-500 shrink-0">
                      {selectedStatusCodes.length} of {statusOptions.length} selected
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            </div>

            {/* Row 2: Email when ready + emails input + Generate */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 pt-3 border-t border-gray-100">
              <label className={`flex items-center gap-2 shrink-0 order-1 ${(fromDate && toDate && getDaysBetween(fromDate, toDate) > ASYNC_THRESHOLD_DAYS) ? "cursor-not-allowed" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={sendOnEmail}
                  onChange={(e) => {
                    const days = getDaysBetween(fromDate, toDate);
                    if (days <= ASYNC_THRESHOLD_DAYS) setSendOnEmail(e.target.checked);
                  }}
                  disabled={fromDate && toDate && getDaysBetween(fromDate, toDate) > ASYNC_THRESHOLD_DAYS}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-70"
                />
                <span className="text-sm text-gray-600">
                  <span className="block">Send report to my email when ready</span>
                  <span className="block text-gray-500">(required for date ranges over 90 days)</span>
                </span>
              </label>
              {sendOnEmail && (
                <div className="w-full sm:flex-1 min-w-0 sm:min-w-[200px] order-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Report Sent To</label>
                  <input
                    type="text"
                    placeholder="email@example.com (comma or space separated for multiple)"
                    className="w-full h-9 sm:h-10 px-3 border border-gray-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none"
                    value={notificationEmails}
                    onChange={(e) => setNotificationEmails(e.target.value)}
                  />
                </div>
              )}
              <div className="hidden sm:block flex-1 min-w-0 order-3" aria-hidden />
              <button
                type="submit"
                disabled={loading}
                className="h-9 sm:h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shrink-0 order-4 w-full sm:w-auto mt-2 sm:mt-0"
              >
                {loading ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                Generate
              </button>
            </div>

            {/* Row 3: Presets in a line below Email when ready */}
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mr-1">Presets</span>
              {[
                { label: "Today", value: "today" },
                { label: "7 days", value: "last7days" },
                { label: "30 days", value: "last30days" },
                { label: "This month", value: "thisMonth" },
                { label: "Last month", value: "lastMonth" },
              ].map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setDatePreset(preset.value)}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-700 rounded-md border border-gray-200 hover:border-blue-300 transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {fromDate && toDate && getDaysBetween(fromDate, toDate) > ASYNC_THRESHOLD_DAYS && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200/80 rounded-md px-3 py-1.5">
                Reports over {ASYNC_THRESHOLD_DAYS} days are sent by email. Add at least one notification email above.
              </p>
            )}
        </div>
      </form>

      {/* Latest Report Card — compact, same style as Generate Report card */}
      {latestReport && (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200/80 overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-blue-700 text-white">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 shrink-0 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-base font-semibold">Latest Generated Report</span>
            </div>
            <span className="px-2 py-0.5 bg-blue-600 text-blue-100 text-xs font-medium rounded">New</span>
          </div>
          <div className="p-4 sm:p-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">Report Type</p>
                <p className="text-sm font-medium text-gray-800">{latestReport.report_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">Generated By</p>
                <p className="text-sm font-medium text-gray-800">{latestReport.report_generated_by}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">Duration</p>
                <p className="text-sm font-medium text-gray-800">{latestReport.generated_from_date} to {latestReport.generated_to_date}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5 uppercase tracking-wider">Generated On</p>
                <p className="text-sm font-medium text-gray-800">{formatDateTime(latestReport.report_generated_date_time)}</p>
              </div>
            </div>
            {latestReport.report_url && (
              <button
                type="button"
                onClick={() => handleDownload(latestReport)}
                className="h-9 sm:h-10 px-5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reports Table Section */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Table Header with Search and Filters */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <h2 className="text-xl font-bold text-gray-800">All Reports History</h2>
            
            <div className="flex flex-col sm:flex-row gap-3 flex-1 lg:max-w-2xl">
              {/* Search Bar */}
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                />
                <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Filter Dropdown */}
              <select
                value={filterReportType}
                onChange={(e) => {
                  setFilterReportType(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
              >
                <option value="all">All Types</option>
                {uniqueReportTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {paginatedReports.length} of {filteredAndSortedReports.length} reports
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {fetchingReports ? (
            <div className="p-12 text-center">
              <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-600 font-medium">Loading reports...</p>
            </div>
          ) : paginatedReports.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    #
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('report_name')}
                  >
                    <div className="flex items-center gap-2">
                      Report Name
                      {sortConfig.key === 'report_name' && (
                        <svg className={`w-4 h-4 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Duration
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('report_generated_date_time')}
                  >
                    <div className="flex items-center gap-2">
                      Generated Date
                      {sortConfig.key === 'report_generated_date_time' && (
                        <svg className={`w-4 h-4 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('report_generated_by')}
                  >
                    <div className="flex items-center gap-2">
                      Generated By
                      {sortConfig.key === 'report_generated_by' && (
                        <svg className={`w-4 h-4 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedReports.map((report, index) => (
                  <tr key={`${report.report_generated_date_time}-${index}`} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                        {report.report_name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">From: {report.generated_from_date}</span>
                        <span className="text-xs text-gray-500">To: {report.generated_to_date}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(report.report_generated_date_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                      {report.report_generated_by}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDownload(report)}
                        disabled={downloadingId === report.report_generated_date_time}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingId === report.report_generated_date_time ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg font-medium mb-2">No Reports Found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 font-medium">Rows per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
                >
                  Next
                </button>
              </div>

              <div className="text-sm text-gray-600">
                Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default Reports;